import React from 'react';
import {
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Grid,
  IconButton,
  Paper,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import InfoIcon from '@mui/icons-material/Info';
import CancelIcon from '@mui/icons-material/Cancel';
import { useDocumentChecklist } from '../../hooks';
import type { IDocumentChecklistProps } from '../../interfaces';

export const DocumentChecklist = ({
  configurations,
  documentRequirements,
  selectedFiles,
  onFileSelect,
  onBatchFileSelect,
  onFileRemoveAtIndex,
  disabled = false,
}: IDocumentChecklistProps) => {
  const checklist = useDocumentChecklist({
    configurations,
    documentRequirements,
    selectedFiles,
    onFileSelect,
    onBatchFileSelect,
    disabled,
  });

  const focusedItem = checklist.activeItems.find((item) => item.key === checklist.focusedType);

  return (
    <Stack spacing={2.5} sx={{ width: '100%' }}>
      <Paper
          variant="outlined"
          onDragOver={(e) => e.preventDefault()}
          onDrop={(e) => {
            e.preventDefault();
            checklist.handleDrop(e.dataTransfer.files);
          }}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            px: 2.5,
            py: 2,
            borderStyle: 'dashed',
            borderWidth: 2,
            borderColor: checklist.allDone ? 'success.main' : 'primary.main',
            bgcolor: checklist.allDone ? 'success.50' : 'primary.50',
            opacity: disabled ? 0.6 : 1,
          }}
      >
        <input
            type="file"
            ref={checklist.fileInputRef}
            multiple
            hidden
            onChange={(e) => {
              checklist.handleFileChange(e.target.files);
              e.target.value = '';
            }}
            disabled={disabled}
        />

        <Avatar
          sx={{
            bgcolor: checklist.allDone ? 'success.main' : 'primary.50',
            color: checklist.allDone ? 'common.white' : 'primary.main',
            width: 44,
            height: 44,
          }}
        >
          <CloudUploadIcon fontSize="small" />
        </Avatar>

        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="body2" fontWeight={700}>
            {checklist.allDone ? 'All documents attached!' : 'Drop files here or click to upload'}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {checklist.allDone
              ? 'You can still add more files to multi-upload slots below'
              : checklist.focusedType
                ? `Uploading next: ${focusedItem?.type || checklist.focusedType}`
                : 'Select multiple files at once to auto-assign them'}
          </Typography>
        </Box>

        <Chip
          label={`${checklist.attachedCount} / ${checklist.activeItems.length}`}
          size="small"
          color={checklist.allDone ? 'success' : 'primary'}
          sx={{ fontWeight: 700 }}
        />
      </Paper>

      {checklist.activeItems.length > 0 && (
        <Box>
          <Typography variant="caption" color="text.secondary" fontWeight={700} sx={{ textTransform: 'uppercase', mb: 1.5, display: 'block' }}>
            Required Documents
          </Typography>
          <Grid container spacing={1.5}>
            {checklist.activeItems.map((item, index) => {
              const uploadedFiles = selectedFiles[item.key] || [];
              const isUploaded = uploadedFiles.length > 0;
              const isLocked = checklist.getIsLocked(item.key, index);
              const isCurrent =
                (item.requirement === 'Mandatory' && index === checklist.firstPendingMandatoryIndex) ||
                (!checklist.hasPendingMandatory && !isUploaded && checklist.focusedType === item.key);
              const isFocused = checklist.focusedType === item.key;
              const clickable = !isLocked && (!isUploaded || item.allowMultiple) && !disabled;

              return (
                <Grid item xs={12} sm={6} lg={3} key={item.key}>
                  <Card
                      variant="outlined"
                      onClick={() => {
                        if (clickable) checklist.setFocusedType(item.key);
                      }}
                      sx={{
                        height: '100%',
                        cursor: clickable ? 'pointer' : 'default',
                        borderWidth: 2,
                        borderColor: isUploaded
                          ? 'info.main'
                          : isFocused && !isUploaded
                            ? 'primary.main'
                            : 'divider',
                        bgcolor: isUploaded
                          ? 'info.50'
                          : isLocked
                            ? 'grey.100'
                            : 'background.paper',
                        opacity: isLocked ? 0.6 : 1,
                      }}
                  >
                    <Box
                        sx={{
                          height: 4,
                          bgcolor: isUploaded
                            ? 'success.main'
                            : isCurrent
                              ? 'success.light'
                              : isLocked
                                ? 'grey.300'
                                : 'transparent',
                        }}
                    />
                    <CardContent sx={{ p: 2 }}>
                      <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1} sx={{ mt: 0.5 }}>
                        <Avatar
                          variant="rounded"
                          sx={{
                            width: 28,
                            height: 28,
                            bgcolor: isUploaded
                              ? 'success.main'
                              : isLocked
                                ? 'grey.300'
                                : isCurrent
                                  ? 'primary.50'
                                  : 'grey.100',
                            color: isUploaded
                              ? 'common.white'
                              : isLocked
                                ? 'text.disabled'
                                : isCurrent
                                  ? 'primary.main'
                                  : 'text.disabled',
                          }}
                        >
                          {isUploaded ? <CheckCircleIcon fontSize="small" /> : <InfoIcon fontSize="small" />}
                        </Avatar>

                        <Stack direction="row" spacing={0.5} sx={{ flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                          {isUploaded && (
                            <Chip label="File Attached" size="small" color="success" sx={{ fontWeight: 700, fontSize: '0.6rem' }} />
                          )}
                          {item.requirement === 'Mandatory' && !isUploaded && (
                            <Chip label="Required" size="small" color="error" sx={{ fontWeight: 700, fontSize: '0.6rem' }} />
                          )}
                          {item.requirement === 'Optional' && !isUploaded && (
                            <Chip label="Optional" size="small" color="info" sx={{ fontWeight: 700, fontSize: '0.6rem' }} />
                          )}
                          {item.allowMultiple && (
                            <Chip label="Multi" size="small" color="secondary" sx={{ fontWeight: 700, fontSize: '0.6rem' }} />
                          )}
                          {isLocked && (
                            <Typography variant="caption" color="text.disabled" fontWeight={700} sx={{ textTransform: 'uppercase' }}>
                              Locked
                            </Typography>
                          )}
                        </Stack>
                      </Stack>

                      <Typography
                        variant="body2"
                        fontWeight={700}
                        sx={{ mt: 1.5, color: isUploaded ? 'info.dark' : isLocked ? 'text.disabled' : 'text.primary' }}
                      >
                        {item.type}
                      </Typography>

                      {isUploaded ? (
                        <Stack spacing={0.5} sx={{ mt: 1 }}>
                          {uploadedFiles.map((file, fileIndex) => (
                            <Paper
                                key={fileIndex}
                                variant="outlined"
                                onClick={(e) => e.stopPropagation()}
                                sx={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'space-between',
                                  gap: 0.5,
                                  px: 1,
                                  py: 0.5,
                                  borderColor: 'info.light',
                                }}
                            >
                              <Typography variant="caption" color="info.dark" fontWeight={600} noWrap>
                                {file.name}
                              </Typography>
                              {onFileRemoveAtIndex && !disabled && (
                                <Tooltip title="Remove this file">
                                  <IconButton
                                      size="small"
                                      aria-label={`Remove ${file.name}`}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        onFileRemoveAtIndex(item.key, fileIndex);
                                      }}
                                      sx={{ p: 0.25 }}
                                  >
                                    <CancelIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              )}
                            </Paper>
                          ))}
                          {item.allowMultiple && !disabled && (
                            <Button
                                variant="text"
                                size="small"
                                color="secondary"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  checklist.setFocusedType(item.key);
                                }}
                                sx={{ alignSelf: 'flex-start', fontWeight: 700, fontSize: '0.65rem' }}
                            >
                              + Add another file
                            </Button>
                          )}
                        </Stack>
                      ) : (
                        <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                          {isLocked
                            ? 'Upload mandatory docs first'
                            : item.allowMultiple
                              ? `You can upload multiple ${item.type} files`
                              : item.description || `Upload your ${item.type}`}
                        </Typography>
                      )}
                    </CardContent>
                  </Card>
                </Grid>
              );
            })}
          </Grid>
        </Box>
      )}

      {checklist.activeItems.length === 0 && (
        <Paper variant="outlined" sx={{ p: 4, textAlign: 'center', borderStyle: 'dashed', bgcolor: 'grey.100' }}>
          <Typography variant="body2" color="text.secondary">
            No specific document requirements.
          </Typography>
        </Paper>
      )}
    </Stack>
  );
};
